import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import FilterMenu from './FilterMenu'
import { Autocomplete, Checkbox, FormControlLabel, ListItem, Radio, RadioGroup, Slider, TextField } from '@mui/material'
import TechnologyLogo from '../Logo/TechnologyLogo'
import { t } from 'i18next'
import CollapsibleSection from '../Button/CollapsibleSection'
import { useGetTechnologiesQuery } from '../../store/redux/reducers/technologiesSlice'
import { RequestProps, Technology, User } from '../../store/types'
import { isEqual } from 'lodash'
import { Box } from '@mui/system'
import { ProjectAcronym, useGetAcronymsQuery } from '../../store/redux/reducers/projectsSlice'
import { useSelector } from 'react-redux'
import { getCurrentUser, getIsLeaderAndNotManagement } from '../../store/redux/reducers/currentUserSlice'
import RoleType from '../../store/types/enums/RoleType'
import { useGetLightUsersQuery } from '../../store/redux/reducers/usersSlice'
import dayjs, { Dayjs } from 'dayjs'
import { DatePicker } from '@mui/x-date-pickers'
import DateFormat from '../../store/types/enums/DateFormat'
import { INTERNAL_PROJECT_NAME, VACATION_PROJECT_NAME } from '../../store/constants/projectAcronyms'
import { useDebouncedScroll } from '../../store/utils/useDebouncedScroll'
import { useLeftPadding } from '../../store/redux/hooks'

type FilterDemandsMenuProps = {
  requestProps: RequestProps
  setRequestProps: React.Dispatch<React.SetStateAction<RequestProps>>
  setPage: Dispatch<SetStateAction<number>>
}

const FilterDemandsMenu = ({ requestProps, setRequestProps, setPage }: FilterDemandsMenuProps) => {
  const leftPadding = useLeftPadding()
  const currentUser = useSelector(getCurrentUser)
  const isLeaderAndNotManagement = useSelector(getIsLeaderAndNotManagement)

  const { data: technologies, isLoading: isTechsLoading } = useGetTechnologiesQuery({ page: 0, size: 500 })
  const [selectedTechnologies, setSelectedTechnologies] = useState<Technology[]>([])
  const [rangeSliderValue, setRangeSliderValue] = useState<number>(50)

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    setRangeSliderValue(newValue as number)
  }

  const [projectsPage, setProjectsPage] = useState(0)
  const [searchedAcronym, setSearchedAcronym] = useState('')
  const projectProps = useMemo(() => {
    return {
      page: projectsPage,
      size: 10,
      'leaders.leader.id': isLeaderAndNotManagement ? currentUser?.id : undefined,
      acronym: searchedAcronym,
      sortByWorkHours: false,
    }
  }, [projectsPage, currentUser?.id, isLeaderAndNotManagement, searchedAcronym])
  const { data: projects } = useGetAcronymsQuery(projectProps)
  const [displayedProjects, setDisplayedProjects] = useState<ProjectAcronym[]>([])
  const [selectedProject, setSelectedProject] = useState<ProjectAcronym | null>(null)

  useEffect(() => {
    if (projects) {
      const filteredProjects = projects.content.filter(
        project => project.acronym !== VACATION_PROJECT_NAME && project.acronym !== INTERNAL_PROJECT_NAME
      )
      setDisplayedProjects(prev => [...new Set([...prev, ...filteredProjects])])
    }
  }, [projects])

  const handleFetchMoreData = useCallback(() => {
    if (!projects?.last) {
      setProjectsPage(prev => prev + 1)
    }
  }, [projects])

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      setSearchedAcronym(value)
    }, 500)
  }, [])

  const onScroll = useDebouncedScroll(handleFetchMoreData)

  const { data: submitters } = useGetLightUsersQuery({
    page: 0,
    size: 50,
    'roles.name': `${RoleType.ProjectLeader},${RoleType.Management}`,
  })
  const [displayedSubmitters, setDisplayedSubmitters] = useState<User[]>([])
  const [selectedSubmitter, setSelectedSubmitter] = useState<User | null>(null)

  useEffect(() => {
    if (submitters) {
      setDisplayedSubmitters(prev => [...new Set([...prev, ...submitters.content])])
    }
  }, [submitters])

  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs | null>(null)
  const [completed, setCompleted] = useState<string>('')

  const handleFiltering = () => {
    let newProps: RequestProps = { ...requestProps, page: 0 }

    newProps = {
      ...newProps,
      'project.id': selectedProject !== null ? selectedProject.id : undefined,
      'technologies.technologyDefinition.id':
        selectedTechnologies.length !== 0
          ? selectedTechnologies.map(technology => technology.id).toString()
          : undefined,
      'numberOfPeople<': rangeSliderValue < 50 ? rangeSliderValue : undefined,
      'submitter.id': selectedSubmitter !== null ? selectedSubmitter.id : undefined,
      startDate: selectedStartDate !== null ? selectedStartDate.format(DateFormat.YearMonthDay) : undefined,
      completed: completed === 'Yes' || completed === 'No' ? completed === 'Yes' : undefined,
    }

    if (isEqual(requestProps, newProps)) return
    setPage(0)
    setRequestProps({ ...newProps })
  }

  const handleClear = () => {
    setSelectedTechnologies([])
    setRangeSliderValue(50)
    setSelectedSubmitter(null)
    setSelectedStartDate(null)
    setSelectedProject(null)
    setCompleted('')
    setPage(0)
    setRequestProps({
      page: 0,
      size: requestProps.size,
    })
  }

  return (
    <FilterMenu filter={handleFiltering} clear={handleClear}>
      <CollapsibleSection text={t('page.demands.table.header.project')}>
        <Autocomplete
          sx={{ pl: leftPadding }}
          value={selectedProject}
          onChange={(_, value, reason) => {
            if (value !== null) {
              setSelectedProject(value)
            } else if (reason === 'clear') {
              setSelectedProject(null)
            }
          }}
          getOptionLabel={project => `${project?.acronym}`}
          options={displayedProjects}
          renderInput={params => {
            const { InputProps, inputProps, InputLabelProps, ...rest } = params
            return (
              <TextField
                InputProps={InputProps}
                inputProps={inputProps}
                onChange={handleInputChange}
                {...rest}
                label={t('page.demands.selectProject')}
                size="small"
              />
            )
          }}
          ListboxProps={{
            style: { maxHeight: 200 },
            onScroll: onScroll,
          }}
        />
      </CollapsibleSection>
      <CollapsibleSection text={t('page.demands.table.header.numberOfPeople')}>
        <Box sx={{ pr: '10.5px', pl: `calc(${leftPadding} + 10.5px)` }}>
          <Slider
            sx={{ width: '100%' }}
            getAriaLabel={() => t('page.demands.sliderLabel')}
            value={rangeSliderValue}
            onChange={handleSliderChange}
            min={1}
            max={50}
            step={1}
            valueLabelDisplay="auto"
          />
        </Box>
      </CollapsibleSection>
      <CollapsibleSection text={t('page.demands.table.header.technology')}>
        <Autocomplete
          multiple
          id="technologies-autocomplete"
          size="small"
          sx={{ pl: leftPadding }}
          disableCloseOnSelect
          limitTags={3}
          loading={isTechsLoading}
          options={technologies ? technologies.content : []}
          value={selectedTechnologies}
          onChange={(_event, value) => {
            setSelectedTechnologies(value)
          }}
          getOptionLabel={option => option.name}
          renderOption={(props, option, { selected }) => {
            return (
              <ListItem {...props} sx={{ gap: 1.5 }}>
                <Checkbox sx={{ ml: -1.5 }} checked={selected} />
                <TechnologyLogo size={24} technology={option} />
                {option.name}
              </ListItem>
            )
          }}
          renderInput={params => (
            <TextField
              size="small"
              fullWidth
              id="technologies-autocomplete"
              inputProps={params.inputProps}
              InputProps={params.InputProps}
              label={t('page.users.filtering.technologies')}
            />
          )}
        />
      </CollapsibleSection>
      <CollapsibleSection text={t('page.demands.table.header.startDate')}>
        <Box sx={{ pl: leftPadding }}>
          <DatePicker
            value={selectedStartDate}
            onChange={e => setSelectedStartDate(e)}
            label={t('components.costSidebar.table.body.startDate')}
            minDate={dayjs('1900-01-01')}
            slotProps={{
              textField: {
                size: 'small',
              },
            }}
            sx={{
              width: '100%',
              svg: {
                color: 'text.mediumEmphasis',
              },
            }}
          />
        </Box>
      </CollapsibleSection>
      <CollapsibleSection text={t('page.demands.table.header.submitter')}>
        <Autocomplete
          sx={{ pl: leftPadding }}
          value={selectedSubmitter}
          onChange={(_, value) => {
            if (value !== null) {
              setSelectedSubmitter(value)
            } else setSelectedSubmitter(null)
          }}
          getOptionLabel={user => `${user?.firstName} ${user?.lastName}`}
          options={displayedSubmitters}
          renderInput={params => {
            const { InputProps, inputProps, InputLabelProps, ...rest } = params
            return (
              <TextField
                InputProps={InputProps}
                inputProps={inputProps}
                {...rest}
                label={t('page.demands.selectSubmitter')}
                size="small"
              />
            )
          }}
          ListboxProps={{
            style: { maxHeight: 200 },
          }}
        />
      </CollapsibleSection>
      <CollapsibleSection text={t('page.demands.table.header.completed')}>
        <RadioGroup value={completed} onChange={(_, value) => setCompleted(value)}>
          <FormControlLabel sx={{ pl: leftPadding }} value="Yes" control={<Radio />} label={t('page.demands.yes')} />
          <FormControlLabel sx={{ pl: leftPadding }} value="No" control={<Radio />} label={t('page.demands.no')} />
        </RadioGroup>
      </CollapsibleSection>
    </FilterMenu>
  )
}

export default FilterDemandsMenu